
import React, { createContext, useContext, useCallback, useRef } from "react";
import { db } from "@/services/firebaseService";
import { fetchUserData } from "@/utils/userUtils";
import { toast } from "sonner";

interface CachedUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  /**
   * True when the lookup failed rather than came back empty — the viewer is not
   * permitted to read this user's document (KB-2291 narrows `users/{userId}` to
   * owner / admin / same-org staff), or Firestore was unreachable. Callers must
   * render this differently from a genuinely absent user (KB-2306).
   */
  unavailable?: boolean;
  timestamp: number;
}

interface UserCacheContextType {
  getUserData: (userId: string) => Promise<CachedUser | null>;
}

// Increased cache time to 1 hour (3600000 milliseconds)
const CACHE_TTL = 1000 * 60 * 60; // 1 hour

const UserCacheContext = createContext<UserCacheContextType | null>(null);

export function UserCacheProvider({ children }: { children: React.ReactNode }) {
  // Using useRef for cache to prevent re-renders on cache updates
  const cache = useRef<Map<string, CachedUser>>(new Map());
  const batchQueue = useRef<Set<string>>(new Set());
  const batchTimeout = useRef<NodeJS.Timeout | null>(null);
  // One in-flight promise per queued id, so getUserData() can resolve with the
  // fetched value instead of returning null and never reporting the outcome.
  const inFlight = useRef<Map<string, Promise<CachedUser | null>>>(new Map());
  const resolvers = useRef<Map<string, (v: CachedUser | null) => void>>(new Map());

  const settle = (id: string, value: CachedUser | null) => {
    const resolve = resolvers.current.get(id);
    if (resolve) resolve(value);
    resolvers.current.delete(id);
    inFlight.current.delete(id);
  };

  const processBatchQueue = useCallback(async () => {
    const queuedIds = Array.from(batchQueue.current);
    batchQueue.current.clear();
    batchTimeout.current = null;

    if (queuedIds.length === 0) return;

    const now = Date.now();
    // Each id resolves independently: one unreadable user must not abort the
    // batch, and a denial is cached as `unavailable` so it is not retried on
    // every render (KB-2306).
    await Promise.all(
      queuedIds.map(async (id) => {
        try {
          const userData = await fetchUserData(db, id);
          const entry: CachedUser | null = userData
            ? {
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                timestamp: now
              }
            : null;
          if (entry) cache.current.set(id, entry);
          settle(id, entry);
        } catch (error) {
          console.error(`Error fetching user data for ${id}:`, error);
          const entry: CachedUser = { unavailable: true, timestamp: now };
          cache.current.set(id, entry);
          settle(id, entry);
        }
      })
    );
  }, []);

  const getUserData = useCallback(async (userId: string): Promise<CachedUser | null> => {
    const now = Date.now();
    const cachedData = cache.current.get(userId);

    if (cachedData && (now - cachedData.timestamp) < CACHE_TTL) {
      return cachedData;
    }

    const existing = inFlight.current.get(userId);
    if (existing) return existing;

    const pending = new Promise<CachedUser | null>((resolve) => {
      resolvers.current.set(userId, resolve);
    });
    inFlight.current.set(userId, pending);

    batchQueue.current.add(userId);

    if (!batchTimeout.current) {
      batchTimeout.current = setTimeout(processBatchQueue, 50); // 50ms batch window
    }

    return pending;
  }, [processBatchQueue]);

  return (
    <UserCacheContext.Provider value={{ getUserData }}>
      {children}
    </UserCacheContext.Provider>
  );
};

export function useUserCache() {
  const context = useContext(UserCacheContext);
  if (!context) {
    throw new Error("useUserCache must be used within a UserCacheProvider");
  }
  return context;
}

/**
 * The null-safe variant: returns the cache when a `UserCacheProvider` is mounted (the whole app, from
 * App.tsx), or `null` when it is not. A consumer that only DECORATES with a resolved name — the KB-3317
 * inbox standing line, rendered in card suites that mount no provider — degrades to no name rather than
 * throwing, and never builds a second user cache (dispatcher ruling 1, KB-3317).
 */
export function useUserCacheOptional(): UserCacheContextType | null {
  return useContext(UserCacheContext);
}
